import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { useGetClubManagementRoles, getClubDetail } from '@/services/club'
import { getClubStaffRestrictions } from '@/services/clubRestrictions'
import { MyClub } from '@/types'

export interface ClubManagementData extends MyClub {
  clubDetails?: any
  hasRestrictions?: boolean
  restrictionsData?: any
  pendingInvites?: number
  isLoadingDetails?: boolean
  detailsError?: any
}

export interface DataFetchingOptions {
  enableDetailedData: boolean
  enableRestrictionsData: boolean
  batchSize: number
}

export const useClubManagementData = (options: Partial<DataFetchingOptions> = {}) => {
  const { enableDetailedData = true, enableRestrictionsData = true } = options

  const { data: clubRoles, isLoading: isLoadingRoles, error: rolesError } = useGetClubManagementRoles()

  // Filter clubs to only management roles (DIRECTOR/ORGANIZER)
  const managementClubs = useMemo(() => {
    if (!clubRoles?.results) return []

    return clubRoles.results.filter((club) => ['DIRECTOR', 'ORGANIZER'].includes(club.role.role))
  }, [clubRoles])

  // Process clubs in batches for performance
  // const clubBatches = useMemo(() => {
  //   const batches = []
  //   for (let i = 0; i < managementClubs.length; i += batchSize) {
  //     batches.push(managementClubs.slice(i, i + batchSize))
  //   }
  //   return batches
  // }, [managementClubs, batchSize])

  // Create optimized queries for club details with improved caching
  const detailQueries = useQueries({
    queries: managementClubs.slice(0, 50).map((club) => ({
      queryKey: ['club-detail', club.clubId.toString()],
      queryFn: async () => {
        return await getClubDetail(club.clubId.toString())
      },
      enabled: !!club.clubId && enableDetailedData,
      cacheTime: 5 * 60 * 1000, // 5 minutes
      staleTime: 2 * 60 * 1000, // 2 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch if data is still fresh
      refetchOnReconnect: 'always' as const, // Refetch when network reconnects
      retry: 2, // Retry failed requests up to 2 times
    })),
  })

  const restrictionQueries = useQueries({
    queries: managementClubs.slice(0, 50).map((club) => ({
      queryKey: ['club-restrictions', club.clubId],
      queryFn: async () => {
        return await getClubStaffRestrictions(club.clubId)
      },
      enabled: !!club.clubId && enableRestrictionsData,
      cacheTime: 5 * 60 * 1000, // 5 minutes
      staleTime: 2 * 60 * 1000, // 2 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch if data is still fresh
      refetchOnReconnect: 'always' as const, // Refetch when network reconnects
      retry: 2, // Retry failed requests up to 2 times
    })),
  })

  // Combine club roles data with additional details
  const combinedClubData = useMemo(() => {
    if (!managementClubs.length) return []

    return managementClubs.map((club, index) => {
      const detailQuery = detailQueries[index]
      const restrictionQuery = restrictionQueries[index]
      const details = detailQuery?.data
      const restrictions = restrictionQuery?.data

      return {
        ...club,
        clubDetails: details || null,
        restrictionsData: restrictions || null,
        hasRestrictions: Array.isArray(restrictions) && restrictions.length > 0,
        pendingInvites: (details as any)?.pendingRequestList?.length || 0,
        isLoadingDetails:
          (enableDetailedData && detailQuery?.isLoading) ||
          (enableRestrictionsData && restrictionQuery?.isLoading) ||
          false,
        detailsError: detailQuery?.error || restrictionQuery?.error || null,
      } as ClubManagementData
    })
  }, [managementClubs, detailQueries, restrictionQueries, enableDetailedData, enableRestrictionsData])

  const isLoading = isLoadingRoles
  const hasError = rolesError

  return {
    data: combinedClubData,
    totalClubs: managementClubs.length,
    isLoading,
    error: hasError,
    refetch: () => {
      detailQueries.forEach((q) => q.refetch())
      restrictionQueries.forEach((q) => q.refetch())
    },
  }
}
